import { TimestampDisplay } from '@/libs/components/src/lib/TimestampDisplay';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { Autocomplete } from '@chiroup/components/form/Autocomplete';
import { Input } from '@chiroup/components/form/Input';
import { Toggle } from '@chiroup/components/form/Toggle';
import { TrivialTooltip } from '@chiroup/components/TrivialTooltip';
import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import {
  DispositionChangeData,
  UnroutedExchangeSummary,
} from '@chiroup/core/types/ConnectorAdmin.type';
import { exchangeDispositionDataSourceLabel } from '@chiroup/core/types/Integration.type';
import { useMemo, useState } from 'react';
import useClinics from '../../hooks/useClinics';
import {
  useAssignUnrouted,
  useCloseUnrouted,
  useReopenUnrouted,
  useUnroutedExchanges,
} from '../../hooks/useConnectionApi';
import connectionApiService from '../../services/connectionApi.service';

const money = (n: number | null) => (n === null ? '' : n.toFixed(2));

/**
 * Columns for the queue list. The list renders denormalized `meta` display
 * fields only — never the payload (contract guarantee). Money right-aligns
 * (feedback_numbers_right_align); the "Matched clinics" column surfaces the
 * candidates the funnel resolved so the router picks an informed clinicId.
 */
const columns: ColumnDef<UnroutedExchangeSummary, unknown>[] = [
  {
    accessorKey: 'receivedAt',
    header: 'Received',
    cell: ({ row }) => (
      <TimestampDisplay ts={row.original.receivedAt} format="YYYY-MM-DD HH:mm:ss" />
    ),
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'vendor',
    header: 'Vendor',
    cell: ({ row }) => row.original.vendor ?? '',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap' },
  },
  {
    accessorKey: 'docType',
    header: 'Doc',
    cell: ({ row }) => row.original.docType ?? '',
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
  { accessorKey: 'reason', header: 'Reason', enableSorting: false },
  {
    accessorKey: 'payorName',
    header: 'Payor',
    cell: ({ row }) => row.original.payorName ?? '',
    enableSorting: false,
  },
  {
    accessorKey: 'payeeNpi',
    header: 'Payee NPI',
    cell: ({ row }) => row.original.payeeNpi ?? '',
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
  {
    accessorKey: 'payeeTin',
    header: 'TIN',
    cell: ({ row }) => row.original.payeeTin ?? '',
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
  {
    accessorKey: 'trn',
    header: 'TRN/Check No.',
    cell: ({ row }) => row.original.trn ?? '',
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
  {
    accessorKey: 'paymentAmount',
    header: 'Amount',
    cell: ({ row }) => money(row.original.paymentAmount),
    enableSorting: false,
    meta: { align: 'right' as const, className: 'tabular-nums font-mono' },
  },
  {
    accessorKey: 'clpCount',
    header: 'CLPs',
    cell: ({ row }) => row.original.clpCount ?? '',
    enableSorting: false,
    meta: { align: 'right' as const, className: 'tabular-nums' },
  },
  {
    id: 'candidates',
    header: 'Matched clinics',
    enableSorting: false,
    cell: ({ row }) => {
      const candidates = row.original.candidates ?? [];

      if (!candidates.length) return <span className="text-gray-400">—</span>;

      return (
        <div className="space-y-0.5">
          {candidates.map(c => (
            <div
              key={c.clinicId}
              className={`whitespace-nowrap ${c.connectorParticipant ? '' : 'text-gray-400'}`}
            >
              <span className="font-mono">{c.clinicId}</span>
              {c.clinicName ? ` · ${c.clinicName}` : ''}
              {c.connectorParticipant ? '' : ' — not set up'}
            </div>
          ))}
        </div>
      );
    },
  },
];

/**
 * Unrouted-document queue (plan §3e.7 / INFRA-5): inbound documents whose
 * clinic could not be determined park here (Unrouted disposition, clinicId 0)
 * — never silently dropped, never guessed. A human routes each row: assign a
 * clinic (re-runs the ingest funnel with clinicId pinned; corroboration still
 * enforced) or close it with a reason. Both are audited server-side.
 *
 * The list renders denormalized `meta` display fields only — never the payload.
 * When the funnel matched candidate clinics for an ambiguous document, they are
 * shown so the assign pick is informed rather than a guess.
 */
/** The close/reopen audit trail for the selected row. */
const HistoryTrail = ({ history }: { history: UnroutedExchangeSummary['history'] }) => {
  if (!history.length) return null;

  return (
    <ul className="space-y-0.5 text-xs text-gray-600">
      {history.map((h, i) => {
        const d = (h.data ?? {}) as DispositionChangeData;

        return (
          <li key={i}>
            <TimestampDisplay ts={d.at} format="YYYY-MM-DD HH:mm" />
            {` — ${d.from} → ${d.to} by ${d.by}: ${h.text}`}
            {d.src ? ` (${exchangeDispositionDataSourceLabel(d.src)})` : ''}
          </li>
        );
      })}
    </ul>
  );
};

export const UnroutedQueue = () => {
  const unrouted = useUnroutedExchanges();
  const assign = useAssignUnrouted();
  const close = useCloseUnrouted();
  const reopen = useReopenUnrouted();

  const rows = unrouted.data?.rows ?? [];
  const openRows = rows.filter(r => r.disposition === 'unrouted');
  const resolvedRows = rows.filter(r => r.disposition === 'resolved');

  const [view, setView] = useState<'open' | 'resolved'>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [assignClinicId, setAssignClinicId] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [reopenMessage, setReopenMessage] = useState('');
  const [pendingAction, setPendingAction] = useState<'assign' | 'close' | 'reopen' | null>(null);
  const [stamping, setStamping] = useState(false);
  const [stampedId, setStampedId] = useState<string | null>(null);

  const viewRows = view === 'open' ? openRows : resolvedRows;
  const selected: UnroutedExchangeSummary | null =
    viewRows.find(r => r.exchangeId === selectedId) ?? null;

  const switchView = (next: 'open' | 'resolved') => {
    setView(next);
    setSelectedId(null);
    setAssignClinicId('');
    setCloseReason('');
    setReopenMessage('');
  };

  // Trace toggle: stamp the exchange so the assign re-run leaves breadcrumbs in
  // the ops console (on = 15 min; off = un-stamp via durationMinutes 0).
  const setTraceStamp = async (on: boolean) => {
    if (!selected) return;

    setStamping(true);

    try {
      await connectionApiService.stampExchange(selected.exchangeId, on ? 15 : 0);

      setStampedId(on ? selected.exchangeId : null);
    } catch {
      window.alert(`Could not ${on ? 'enable' : 'disable'} tracing for '${selected.exchangeId}'.`);
    } finally {
      setStamping(false);
    }
  };

  const clinicIdNum = Number(assignClinicId.trim());
  const canAssign = !!selected && Number.isInteger(clinicIdNum) && clinicIdNum > 0;

  // Clinic-name typeahead so a worker who doesn't know the clinicId can find it
  // and fill the box. Lazy (skipEmptySearch) — no query until they type.
  const {
    data: clinicsData,
    isFetching: isFetchingClinics,
    fetchNextPage: fetchNextClinics,
    hasNextPage: hasNextClinics,
    onSearch: onSearchClinics,
    searchQuery: clinicSearchQuery,
  } = useClinics({ skipEmptySearch: true });

  const clinicOptions = useMemo(
    () =>
      (clinicsData?.pages?.flatMap(p => p?.data ?? []) ?? [])
        .filter(Boolean)
        .map(c => ({ text: `${c.name} (#${c.ID})`, value: c.ID })),
    [clinicsData?.pages]
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          Unrouted queue{' '}
          <span className="text-base font-normal text-gray-500">
            {unrouted.isLoading ? '…' : `${openRows.length} open · ${resolvedRows.length} resolved`}
          </span>
        </h1>
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          color={view === 'open' ? ButtonColors.primary : ButtonColors.plain}
          onClick={() => switchView('open')}
          text="Open"
          badge={openRows.length}
        />
        <Button
          size="sm"
          color={view === 'resolved' ? ButtonColors.primary : ButtonColors.plain}
          onClick={() => switchView('resolved')}
          text="Resolved"
          badge={resolvedRows.length}
        />
      </div>

      {unrouted.isError ? (
        <p className="text-sm text-red-600">
          Could not load the queue — the endpoint is unreachable.
        </p>
      ) : (
        <div className="[&_td]:align-top!">
          <DataTable
            columns={columns}
            data={viewRows}
            size="compact"
            loading={unrouted.isLoading}
            onRowClick={r => setSelectedId(r.exchangeId)}
            rowClassName={row =>
              `cursor-pointer ${row.original.exchangeId === selectedId ? 'bg-blue-50' : ''}`
            }
            pagination={{ pageSize: 50, pageSizeOptions: [50, 100] }}
            emptyState={
              <p className="text-sm text-gray-500">
                {view === 'open'
                  ? 'No open documents — every inbound document routed to a clinic.'
                  : 'No resolved documents.'}
              </p>
            }
          />
        </div>
      )}

      {selected ? (
        <div className="mt-4 space-y-3 border-t pt-4">
          <div className="text-sm">
            <span className="font-mono">{selected.exchangeId}</span>
          </div>

          <HistoryTrail history={selected.history} />

          {selected.disposition === 'resolved' ? (
            // Resolved rows can only be REOPENED (back to Unrouted) — clinicId 0
            // has no other surface, so the queue is where a reversed decision lands.
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-96">
                <Input
                  name="unrouted-reopen-message"
                  label="Reopen reason (required)"
                  value={reopenMessage}
                  onChange={(val: string) => setReopenMessage(val)}
                />
              </div>
              <Button
                color={ButtonColors.primary}
                loading={reopen.isLoading}
                disabled={!reopenMessage.trim()}
                onClick={() => setPendingAction('reopen')}
                text="Reopen"
              />
              <Button
                color={ButtonColors.plain}
                onClick={() => {
                  setSelectedId(null);
                  setReopenMessage('');
                }}
                text="Cancel"
              />
            </div>
          ) : (
            <>
              {selected.candidates.length ? (
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-xs text-gray-500">Matched clinics — click to assign:</span>
                  {selected.candidates.map(c => (
                    <Button
                      key={c.clinicId}
                      size="sm"
                      color={ButtonColors.plain}
                      // Shown either way, but a clinic with no api Integration row for
                      // this vendor cannot own connector traffic (the endpoint enforces it too).
                      disabled={!c.connectorParticipant}
                      tooltip={
                        c.connectorParticipant
                          ? undefined
                          : `Clinic ${c.clinicId} is not set up to receive ${
                              selected.vendor ?? 'this vendor'
                            } — it can't be assigned this document.`
                      }
                      onClick={() => setAssignClinicId(String(c.clinicId))}
                      text={
                        `${c.clinicId}` +
                        (c.clinicName ? ` · ${c.clinicName}` : '') +
                        (c.npi ? ` (NPI ${c.npi})` : '') +
                        (c.connectorParticipant ? '' : ' — not set up')
                      }
                    />
                  ))}
                </div>
              ) : null}

              <div className="w-96">
                <Autocomplete
                  name="unrouted-clinic-search"
                  label="Don't know the id? Find a clinic by name"
                  className="w-full"
                  // Controlled by the same id the box holds, so picking sticks
                  // (a constant value never registers the selection in HeadlessUI).
                  value={assignClinicId ? Number(assignClinicId) : ''}
                  limit={1}
                  clearable
                  options={clinicOptions}
                  isFetching={isFetchingClinics}
                  fetchNextPage={fetchNextClinics}
                  hasNextPage={hasNextClinics}
                  onChangeSearch={onSearchClinics}
                  searchTerm={clinicSearchQuery}
                  serverSide
                  placeholder="Search clinics…"
                  onChange={val => setAssignClinicId(val ? String(val) : '')}
                />
              </div>

              <div className="flex flex-wrap items-end gap-2">
                <div className="flex flex-col">
                  <span className="block text-sm font-medium leading-5 text-gray-900 dark:text-dark-gray-200">
                    Trace
                    <TrivialTooltip
                      text="Stamp this exchange for 15 minutes of tracing before you assign — the re-run's steps then show up in the ops console trace log."
                      type="info"
                    />
                  </span>
                  <div className="flex h-9.5 items-center">
                    <Toggle
                      value={stampedId === selected.exchangeId}
                      disabled={stamping}
                      onChange={on => setTraceStamp(on)}
                    />
                  </div>
                </div>
                <div className="w-40">
                  <Input
                    name="unrouted-clinic-id"
                    label="Assign to clinic id"
                    value={assignClinicId}
                    onChange={(val: string) => setAssignClinicId(val)}
                  />
                </div>
                <Button
                  color={ButtonColors.primary}
                  loading={assign.isLoading}
                  disabled={!canAssign}
                  onClick={() => setPendingAction('assign')}
                  text="Assign"
                />
                <div className="w-96">
                  <Input
                    name="unrouted-resolve-reason"
                    label="Resolve reason (required)"
                    value={closeReason}
                    onChange={(val: string) => setCloseReason(val)}
                  />
                </div>
                <Button
                  color={ButtonColors.red}
                  loading={close.isLoading}
                  disabled={!closeReason.trim()}
                  onClick={() => setPendingAction('close')}
                  text="Resolve"
                />
                <Button
                  color={ButtonColors.plain}
                  onClick={() => {
                    setSelectedId(null);
                    setAssignClinicId('');
                    setCloseReason('');
                  }}
                  text="Cancel"
                />
              </div>
            </>
          )}
        </div>
      ) : viewRows.length ? (
        <p className="mt-3 text-xs text-gray-500">
          {view === 'open' ? 'Select a row to assign or close it.' : 'Select a row to reopen it.'}
        </p>
      ) : null}

      <ConfirmModal
        isOpen={!!pendingAction}
        close={() => setPendingAction(null)}
        title={
          pendingAction === 'assign'
            ? 'Assign this document?'
            : pendingAction === 'close'
              ? 'Resolve this document?'
              : 'Reopen this document?'
        }
        description={
          !selected
            ? undefined
            : pendingAction === 'assign'
              ? `Assign ${selected.exchangeId} to clinic ${clinicIdNum} and re-run routing? Corroboration still applies — a clinic the document doesn't corroborate to is refused and it stays queued.`
              : pendingAction === 'close'
                ? `Resolve ${selected.exchangeId} without routing it? It moves to the Resolved tab (reversible via reopen).`
                : `Reopen ${selected.exchangeId} back into the queue?`
        }
        confirmText={
          pendingAction === 'assign' ? 'Assign' : pendingAction === 'close' ? 'Resolve' : 'Reopen'
        }
        isDelete={pendingAction === 'close'}
        confirm={() => {
          if (selected && pendingAction === 'assign') {
            assign.mutate(
              { exchangeId: selected.exchangeId, clinicId: clinicIdNum },
              { onSuccess: () => setAssignClinicId('') }
            );
          } else if (selected && pendingAction === 'close') {
            close.mutate(
              { exchangeId: selected.exchangeId, reason: closeReason.trim() },
              { onSuccess: () => setCloseReason('') }
            );
          } else if (selected && pendingAction === 'reopen') {
            reopen.mutate(
              { exchangeId: selected.exchangeId, message: reopenMessage.trim() },
              { onSuccess: () => setReopenMessage('') }
            );
          }

          setPendingAction(null);
        }}
      />
    </div>
  );
};

export default UnroutedQueue;
